/**
 * =============================================================================
 * ACTION DETAIL CARD COMPONENT
 * =============================================================================
 * 
 * Shows detailed action information for confirmation.
 * Appears when user clicks an action button.
 * 
 * DATA FROM API: fetchActionDetails() response
 * - Line 65: details.label → Action title
 * - Line 70: details.description → What will happen
 * - Line 85: details.affectedData → Data being affected (table view)
 * - Line 110: details.confirmationMessage → Warning message
 * 
 * INTERACTIONS:
 * - Line 140: Edit button → calls onEdit() to open iframe
 * - Line 150: Confirm button → calls onConfirm() → triggers executeAlertAction()
 * - Line 160: Reject button → calls onReject() → adds log to chat
 */

import { Box, Stack, Text, Button, Group, Badge } from '@mantine/core';
import { CheckCircle, XCircle, Pencil, Warning, Lightning } from '@phosphor-icons/react';
import { memo } from 'react';
import { formatMoney, formatCount } from '@utils/currency';
import { motion } from 'framer-motion';
import type { AlertAction, ActionDetails, SupportingDataPoint } from './types';

interface ActionDetailCardProps {
    action: AlertAction;
    details: ActionDetails;
    onConfirm: () => void;
    onReject: () => void;
    onEdit?: () => void;
    isLoading: boolean;
}

// =============================================================================
// HELPER: Format value
// =============================================================================

const formatValue = (point: SupportingDataPoint): string => {
    const { value, format } = point;
    switch (format) {
        case 'currency':
            return formatMoney(Number(value));
        case 'percentage':
            return `${value}%`;
        case 'number':
            return formatCount(Number(value));
        case 'date':
            return new Date(String(value)).toLocaleDateString('en-IN');
        default:
            return String(value);
    }
};

// =============================================================================
// COMPONENT
// =============================================================================

const ActionDetailCard: React.FC<ActionDetailCardProps> = memo(({
    action,
    details,
    onConfirm,
    onReject,
    onEdit,
    isLoading,
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
        >
            <Box className="rounded-xl border-2 border-mustard-800/30 bg-mustard-800/5 overflow-hidden">
                {/* Header */}
                <Box className="bg-mustard-800/10 px-4 py-3 border-b border-mustard-800/20">
                    <Group className="justify-between items-center">
                        <Group className="gap-2">
                            <Lightning size={18} weight="fill" className="text-mustard-900" />
                            {/* ====================================================
                                ACTION LABEL - Mapped from details.label - Line 65
                                ==================================================== */}
                            <Text className="text-sm font-semibold text-mustard-900">
                                {details.label || action.label}
                            </Text>
                        </Group>
                        <Badge size="sm" color="yellow" variant="light">
                            Requires Confirmation
                        </Badge>
                    </Group>
                </Box>

                <Stack className="gap-4 p-4">
                    {/* ========================================================
                        DESCRIPTION - Mapped from details.description - Line 70
                        ======================================================== */}
                    {details.description && (
                        <Text className="text-sm text-gray-700">
                            {details.description}
                        </Text>
                    )}

                    {/* ========================================================
                        AFFECTED DATA - Mapped from details.affectedData - Line 85
                        Shows what data will be created/modified by this action
                        ======================================================== */}
                    {details.affectedData && details.affectedData.length > 0 && (
                        <Box 
                            style={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: 12,
                                border: '1px solid rgba(0, 0, 0, 0.06)',
                                overflow: 'hidden',
                            }}
                        >
                            <Text 
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                    color: 'rgb(100, 116, 139)',
                                    padding: '10px 16px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                }}
                            >
                                This action will affect
                            </Text>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    {/* ============================================
                                        FIELD MAPPING for Affected Data:
                                        - point.label → Row label
                                        - point.value → Row value
                                        - point.format → Formatting
                                        ============================================ */}
                                    {details.affectedData.map((point) => (
                                        <tr key={point.key}>
                                            <td 
                                                style={{
                                                    padding: '10px 16px',
                                                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    color: 'rgb(71, 85, 105)',
                                                    width: '33%',
                                                }}
                                            >
                                                {point.label}
                                            </td>
                                            <td 
                                                style={{
                                                    padding: '10px 16px',
                                                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: 'rgb(15, 23, 42)',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatValue(point)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    )}

                    {/* ========================================================
                        CONFIRMATION MESSAGE - Line 110
                        Mapped from details.confirmationMessage
                        ======================================================== */}
                    {details.confirmationMessage && (
                        <Box className="flex items-start gap-2 p-3 rounded-lg bg-mustard-800/10 border border-mustard-800/20">
                            <Warning size={16} weight="fill" className="text-mustard-900 flex-shrink-0 mt-0.5" />
                            <Text className="text-sm text-mustard-900">
                                {details.confirmationMessage}
                            </Text>
                        </Box>
                    )}

                    {/* ========================================================
                        ACTION BUTTONS
                        ======================================================== */}
                    <Group className="justify-end gap-2 pt-1">
                        {/* ====================================================
                            EDIT BUTTON - Line 140
                            Only shown if action.isEditable is true
                            Calls onEdit() → opens IframeEditModal
                            ==================================================== */}
                        {onEdit && (
                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                leftSection={<Pencil size={14} />}
                                onClick={onEdit}
                                disabled={isLoading}
                            >
                                Edit
                            </Button>
                        )}

                        {/* ====================================================
                            REJECT BUTTON - Line 160
                            Calls onReject() → adds log to chat, dismisses card
                            ==================================================== */}
                        <Button
                            variant="light"
                            color="red"
                            size="sm"
                            leftSection={<XCircle size={14} weight="fill" />}
                            onClick={onReject}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>

                        {/* ====================================================
                            CONFIRM BUTTON - Line 150
                            Calls onConfirm() → triggers:
                            1. Log entry "Confirmed: {action.label}"
                            2. API call executeAlertAction()
                            3. Result message in chat
                            ==================================================== */}
                        <Button
                            variant="filled"
                            color="green"
                            size="sm"
                            leftSection={<CheckCircle size={14} weight="fill" />}
                            onClick={onConfirm}
                            loading={isLoading}
                        >
                            Confirm
                        </Button>
                    </Group>
                </Stack>
            </Box>
        </motion.div>
    );
});

ActionDetailCard.displayName = 'ActionDetailCard';

export default ActionDetailCard;
